import { Routes } from '@angular/router';
import { LayoutComponent } from './shared/components/layout/layout.component';
import { Auth } from './features/auth/auth';
import { authGuard, publicGuard } from './core/guards/auth.guard';
import { actionGuard } from './core/guards/action.guard';
import { ACTIONS } from './core/constants/actions.constants';
import { ClassControlLayout } from './features/crm/class-control/class-control-layout';
import { GroupLayout } from './features/crm/groups/group-layout';
import { SettingsLayout } from './features/crm/settings/settings-layout';
import { PUBLIC_ROUTES } from './features/public/public.routes';

export const routes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('./core/components/redirect-to-last-visited/redirect-to-last-visited').then(m => m.RedirectToLastVisited)
    },
    {
        path: 'google-calendar/callback',
        loadComponent: () => import('./features/crm/google-calendar-callback/google-calendar-callback.component').then(m => m.GoogleCalendarCallbackComponent)
    },
    {
        path: 'google-contacts/callback',
        loadComponent: () => import('./features/crm/google-contacts-callback/google-contacts-callback.component').then(m => m.GoogleContactsCallbackComponent)
    },
    {
        path: 'crm',
        component: LayoutComponent,
        canActivate: [authGuard],
        children: [
            {
                path: '',
                loadComponent: () => import('./features/crm/crm').then(m => m.Crm),
                children: [
                    {
                        path: 'university-plans',
                        loadComponent: () => import('./features/crm/university-plans/university-plans').then(m => m.UniversityPlans),
                        canActivate: [actionGuard],
                        data: { requiredAction: ACTIONS.VIEW_UNIVERSITY_SUBJECTS }
                    },
                    {
                        path: 'year-course-letter',
                        loadComponent: () => import('./features/crm/year-course-letter/year-course-letter').then(m => m.YearCourseLetter),
                        canActivate: [actionGuard],
                        data: { requiredAction: ACTIONS.VIEW_COURSE_LETTERS }
                    },
                    {
                        path: 'teachers',
                        loadComponent: () => import('./features/crm/teachers/teachers').then(m => m.Teachers),
                        canActivate: [actionGuard],
                        data: { requiredAction: ACTIONS.VIEW_TEACHERS }
                    },
                    {
                        path: 'academies',
                        loadComponent: () => import('./features/crm/academies/academies').then(m => m.Academies),
                        canActivate: [actionGuard],
                        data: { requiredAction: ACTIONS.VIEW_ACADEMIES }
                    },
                    {
                        path: 'students/all',
                        loadComponent: () => import('./features/crm/students/students').then(m => m.Students),
                        canActivate: [actionGuard],
                        data: { requiredAction: ACTIONS.VIEW_STUDENTS }
                    },
                    {
                        path: 'students/auto',
                        loadComponent: () => import('./features/crm/students/students-auto/students-auto').then(m => m.StudentsAuto),
                        canActivate: [actionGuard],
                        data: { requiredAction: ACTIONS.VIEW_STUDENTS }
                    },
                    {
                        path: 'tasks',
                        loadComponent: () => import('./features/crm/tasks/tasks').then(m => m.Tasks),
                        canActivate: [actionGuard],
                        data: { requiredAction: ACTIONS.VIEW_TASKS }
                    },
                    {
                        path: 'class-control',
                        component: ClassControlLayout,
                        canActivate: [actionGuard],
                        data: { requiredAction: ACTIONS.VIEW_CLASSES },
                        children: [
                            {
                                path: 'wizard',
                                loadComponent: () => import('./features/crm/class-control/class-control-wizard/class-control-wizard').then(m => m.ClassControlWizard),
                            },
                            {
                                path: '',
                                loadComponent: () => import('./features/crm/class-control/class-control').then(m => m.ClassControl)
                            }
                        ]
                    },
                    {
                        path: 'groups',
                        component: GroupLayout,
                        canActivate: [actionGuard],
                        data: { requiredAction: ACTIONS.VIEW_GROUPS },
                        children: [
                            {
                                path: '',
                                loadComponent: () => import('./features/crm/groups/groups').then(m => m.Groups)
                            },
                            {
                                path: 'link-stats',
                                loadComponent: () => import('./features/crm/groups/group-link-stats/group-link-stats').then(m => m.GroupLinkStats),
                                canActivate: [actionGuard],
                                data: { requiredAction: ACTIONS.SHARE_GROUP_LINK }
                            },
                            {
                                path: 'details/:id',
                                loadComponent: () => import('./features/crm/groups/group-details/group-details').then(m => m.GroupDetails),
                            },
                            {
                                path: 'subject/:id',
                                loadComponent: () => import('./features/crm/groups/subject-group-details/subject-group-details').then(m => m.SubjectGroupDetails),
                                canActivate: [actionGuard],
                                data: { requiredAction: ACTIONS.VIEW_SUBJECT_GROUPS }
                            }
                        ]
                    },
                    {
                        path: 'rates',
                        loadComponent: () => import('./features/crm/rates/rates').then(m => m.RatesComponent),
                        canActivate: [actionGuard],
                        data: { requiredAction: ACTIONS.VIEW_RATES }
                    },
                    {
                        path: 'treasury/cash-control',
                        loadComponent: () => import('./features/crm/treasury/cash-control/cash-control').then(m => m.CashControl),
                        canActivate: [actionGuard],
                        data: { requiredAction: ACTIONS.VIEW_CASH_CONTROL }
                    },
                    {
                        path: 'treasury/bank-match',
                        loadComponent: () => import('./features/crm/treasury/bank-match/bank-match').then(m => m.BankMatch),
                        canActivate: [actionGuard],
                        data: { requiredAction: ACTIONS.VIEW_BANK_MATCH }
                    },
                    {
                        path: 'treasury/bank-match/discarded',
                        loadComponent: () => import('./features/crm/treasury/bank-match/discarded-movements/discarded-movements').then(m => m.DiscardedMovements),
                        canActivate: [actionGuard],
                        data: { requiredAction: ACTIONS.VIEW_BANK_MATCH }
                    },
                    {
                        path: 'treasury/bank-match/conciliated',
                        loadComponent: () => import('./features/crm/treasury/bank-match/conciliated-movements/conciliated-movements').then(m => m.ConciliatedMovements),
                        canActivate: [actionGuard],
                        data: { requiredAction: ACTIONS.VIEW_BANK_MATCH }
                    },
                    {
                        path: 'settings',
                        component: SettingsLayout,
                        canActivate: [authGuard, actionGuard],
                        data: { requiredAction: ACTIONS.VIEW_SETTINGS },
                        children: [
                            {
                                path: '',
                                children: [
                                    {
                                        path: 'academic-structure/universities',
                                        loadComponent: () => import('./features/crm/settings/academic-structure/universities/universities').then(m => m.Universities),
                                        canActivate: [actionGuard],
                                        data: { requiredAction: ACTIONS.VIEW_UNIVERSITIES }
                                    },
                                    {
                                        path: 'academic-structure/faculties',
                                        loadComponent: () => import('./features/crm/settings/academic-structure/faculties/faculties').then(m => m.Faculties),
                                        canActivate: [actionGuard],
                                        data: { requiredAction: ACTIONS.VIEW_FACULTIES }
                                    },
                                    {
                                        path: 'academic-structure/school-courses',
                                        loadComponent: () => import('./features/crm/settings/academic-structure/school-courses/school-courses').then(m => m.SchoolCourses),
                                        canActivate: [actionGuard],
                                        data: { requiredAction: ACTIONS.VIEW_SCHOOL_COURSES }
                                    },
                                    {
                                        path: 'academic-structure/university-careers',
                                        loadComponent: () => import('./features/crm/settings/academic-structure/university-careers/university-careers').then(m => m.UniversityCareers),
                                        canActivate: [actionGuard],
                                        data: { requiredAction: ACTIONS.VIEW_UNIVERSITY_CAREERS }
                                    },
                                    {
                                        path: 'academic-structure/unidegrees',
                                        loadComponent: () => import('./features/crm/settings/academic-structure/unidegree/unidegrees').then(m => m.Unidegrees),
                                        canActivate: [actionGuard],
                                        data: { requiredAction: ACTIONS.VIEW_UNIDEGREES }
                                    },
                                ]
                            },
                            {
                                path: 'roles-and-permissions',
                                loadComponent: () => import('./features/crm/settings/academic-structure/roles-and-permissions/roles-and-permissions').then(m => m.RolesAndPermissionsComponent),
                                canActivate: [actionGuard],
                                data: { requiredAction: ACTIONS.EDIT_ROLES_PERMISSIONS }
                            },
                            {
                                path: 'integrations',
                                loadComponent: () => import('./features/crm/settings/integrations/integrations').then(m => m.IntegrationsComponent),
                            },
                            {
                                path: 'audit-log',
                                loadComponent: () => import('./features/crm/settings/audit-log/audit-log').then(m => m.AuditLogComponent),
                                canActivate: [actionGuard],
                                data: { requiredAction: ACTIONS.VIEW_AUDIT_LOG }
                            },
                            {
                                path: 'recording-history',
                                loadComponent: () => import('./features/crm/settings/recording-history/recording-history').then(m => m.RecordingHistoryComponent),
                                canActivate: [actionGuard],
                                data: { requiredAction: ACTIONS.VIEW_RECORDING_HISTORY }
                            },
                            {
                                path: 'constants',
                                loadComponent: () => import('./features/crm/settings/constants/constants').then(m => m.ConstantsComponent),
                                canActivate: [actionGuard],
                                data: { requiredAction: ACTIONS.EDIT_ROLES_PERMISSIONS }
                            },
                            {
                                path: 'notifications',
                                loadComponent: () => import('./features/crm/notifications/notifications').then(m => m.Notifications),
                                canActivate: [actionGuard],
                                data: { requiredAction: ACTIONS.VIEW_NOTIFICATIONS }
                            },
                            {
                                path: 'notifications/types',
                                loadComponent: () => import('./features/crm/notifications/notification-types/notification-types').then(m => m.NotificationTypes),
                                canActivate: [actionGuard],
                                data: { requiredAction: ACTIONS.CONFIG_NOTIFICATION_TYPES }
                            }
                        ]
                    },
                    {
                        path: 'unauthorized',
                        loadComponent: () => import('./shared/components/unauthorized/unauthorized').then(m => m.UnauthorizedComponent)
                    }
                ]
            },
        ]
    },
    {
        path: 'public',
        children: PUBLIC_ROUTES
    },
    {
        path: 'auth',
        component: Auth,
        canActivate: [publicGuard],
        children: [
            {
                path: '',
                redirectTo: 'login',
                pathMatch: 'full'
            },
            {
                path: 'login',
                loadComponent: () => import('./features/auth/login/login').then(m => m.Login)
            }
        ]
    },
    {
        path: '**',
        redirectTo: '/crm/class-control',
        pathMatch: 'full'
    }
];
